import { useEffect, useRef } from "react";
import { useWSInfiniteQuery, useWSQuery } from "../../query/helpers";

type Query = ReturnType<typeof useWSQuery | typeof useWSInfiniteQuery>;

type QueryWithData<T extends readonly Query[]> = {
  [K in keyof T]: T[K] extends { data: infer D }
    ? T[K] & { data: NonNullable<D> }
    : never;
};

export const useQueriesOn = <
  TQueries extends [Query, ...Query[]],
  Event extends "successAll" | "error" | "fetchedAll"
>(
  event: Event,
  callback: (
    ...queries: Event extends "successAll" ? QueryWithData<TQueries> : TQueries
  ) => void,
  ...queries: TQueries
) => {
  const isTriggered = useRef(false);

  const isFetched = queries.every(query => query.isFetched);

  useEffect(() => {
    if (isFetched && !isTriggered.current) {
      isTriggered.current = true;

      const isSuccess = queries.every(query => query.isSuccess);
      const isError = queries.some(query => query.isError);

      if (event === "successAll" && !isSuccess) return;
      if (event === "error" && !isError) return;

      callback(
        ...(queries as unknown as Event extends "successAll"
          ? QueryWithData<TQueries>
          : TQueries)
      );
    }
  }, [isFetched, callback, event, queries]);
};
